import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Loader2, Settings2, Landmark, Clock3, ShieldAlert, Lock } from "lucide-react";
import { toast } from "sonner";
import { loadAusRepaymentSettings, saveAusRepaymentSettings } from "@/lib/api";
import { fmtSydney } from "./LoanRepaymentsAusParts";

const REFRESH_OPTIONS = [[0, "Off"], [60, "Every 1 min"], [120, "Every 2 min"], [300, "Every 5 min"], [600, "Every 10 min"], [1800, "Every 30 min"]];
const POLL_OPTIONS = [5, 10, 15, 30, 60, 120, 240];

function SyncLine({ label, run, testid }) {
  if (!run) return <p className="text-[11px] text-muted-foreground" data-testid={testid}>{label}: never run</p>;
  const ok = run.status === "done";
  return (
    <p className="text-[11px] text-muted-foreground" data-testid={testid}>
      {label}: <b className={ok ? "text-emerald-700" : run.status === "running" ? "text-amber-700" : "text-red-700"}>{run.status}</b>
      {" · "}{fmtSydney(run.finished_at || run.started_at)} Sydney
      {ok && run.payments != null && ` · ${run.payments} payments`}
      {ok && run.deposits != null && ` · ${run.deposits} deposits · ${run.matched ?? 0} matched`}
      {run.error && <span className="text-red-700"> · {run.error}</span>}
    </p>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function AusRepaymentSettingsSheet({ open, onOpenChange, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const canEdit = !!data?.can_edit;

  useEffect(() => {
    if (!open) return;
    setData(null);
    loadAusRepaymentSettings().then((d) => {
      setData(d);
      setForm({ method_id: d.method_id, payto_method_id: d.payto_method_id, collector_id: d.collector_id, capay_method_id: d.capay_method_id, capay_collector_id: d.capay_collector_id,
        page_refresh_seconds: d.page_refresh_seconds, capay_poll_minutes: d.capay_poll_minutes, gc_sync_time: d.gc_sync_time, capay_mode: d.capay_mode,
        dd_post_stage: d.dd_post_stage || "confirmed", gc_intraday_minutes: d.gc_intraday_minutes || 30 });
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load settings"));
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const num = (k) => ({ value: form[k] ?? "", onChange: set(k), type: "number", min: 1, disabled: !canEdit, className: "h-8 text-sm" });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        method_id: Number(form.method_id), payto_method_id: Number(form.payto_method_id), collector_id: Number(form.collector_id),
        capay_method_id: Number(form.capay_method_id), capay_collector_id: Number(form.capay_collector_id),
        page_refresh_seconds: Number(form.page_refresh_seconds), capay_poll_minutes: Number(form.capay_poll_minutes),
        gc_sync_time: form.gc_sync_time, dd_post_stage: form.dd_post_stage, gc_intraday_minutes: Number(form.gc_intraday_minutes),
        ...(form.capay_mode !== data.capay_mode ? { capay_mode: form.capay_mode } : {}),
      };
      if (payload.capay_mode === "live" && !window.confirm("Switch Capay to LIVE? Real bank credits will start flowing into this page and LoanDisk postings will use them.")) { setSaving(false); return; }
      const r = await saveAusRepaymentSettings(payload);
      toast.success("Settings saved" + (r.updated?.includes("capay_poll_minutes") || r.updated?.includes("gc_sync_time") ? " · schedules updated" : ""));
      setData(r);
      onSaved?.(r);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px] sm:max-w-[440px] overflow-y-auto" data-testid="lra-settings-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4" />Loan Repayments settings <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">AUS</Badge></SheetTitle>
          <SheetDescription>
            {canEdit ? "Timers, LoanDisk posting ids and the Capay environment for this page." : "Read-only — ask an Admin to change these."}
          </SheetDescription>
        </SheetHeader>
        {!data ? (
          <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="space-y-6 py-4">
            {!canEdit && (
              <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="lra-settings-readonly-note">
                <Lock className="w-3.5 h-3.5" />Admins only can edit. You can still see what is configured.
              </div>
            )}

            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" />Refresh timers</h4>
              <Field label="Page auto-refresh" hint="Reloads the tables silently in the background while the page is open.">
                <select value={form.page_refresh_seconds ?? 300} onChange={set("page_refresh_seconds")} disabled={!canEdit} className="h-8 w-full text-sm border rounded px-2 bg-white" data-testid="lra-settings-page-refresh">
                  {REFRESH_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Capay bank feed poll" hint="How often the portal pulls new bank credits from Capay. Webhooks still land instantly.">
                <select value={form.capay_poll_minutes ?? 30} onChange={set("capay_poll_minutes")} disabled={!canEdit} className="h-8 w-full text-sm border rounded px-2 bg-white" data-testid="lra-settings-capay-poll">
                  {POLL_OPTIONS.map((m) => <option key={m} value={m}>Every {m >= 60 ? `${m / 60} h` : `${m} min`}</option>)}
                </select>
              </Field>
              <Field label="GoCardless AU intraday re-sync" hint="Light 3-day re-sync so today's PayTo confirmations and BECS status flips appear during the day. The page also self-syncs when it is opened on a recent date and the mirror is older than 10 minutes.">
                <select value={form.gc_intraday_minutes ?? 30} onChange={set("gc_intraday_minutes")} disabled={!canEdit} className="h-8 w-full text-sm border rounded px-2 bg-white" data-testid="lra-settings-gc-intraday">
                  {[10, 15, 30, 60, 120].map((m) => <option key={m} value={m}>Every {m} min</option>)}
                </select>
              </Field>
              <Field label="GoCardless AU full daily sync (Sydney time)" hint="Full 10-day re-mirror of PayTo / Direct Debit results + payouts. GoCardless creates the day's payout ~05:30, so 06:00 is the earliest sensible time.">
                <Input type="time" value={form.gc_sync_time || "05:00"} onChange={set("gc_sync_time")} disabled={!canEdit} className="h-8 text-sm w-36" data-testid="lra-settings-gc-time" />
              </Field>
            </section>

            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" />Direct Debit posting stage</h4>
              <Field label="Post BECS Direct Debits when…" hint="Confirmed = the team's daily routine (post today everything GoCardless has confirmed). Anything that has already flipped to failed is never offered, and anything posted that fails later is flagged in the red banner + Slack for a one-click reversal. 'Paid out' is the cautious alternative (waits ≈1 more business day for the money to land). PayTo is real-time and always posts at confirmed.">
                <select value={form.dd_post_stage || "confirmed"} onChange={set("dd_post_stage")} disabled={!canEdit} className="h-8 w-full text-sm border rounded px-2 bg-white" data-testid="lra-settings-dd-stage">
                  <option value="confirmed">Confirmed — post today (default)</option>
                  <option value="paid_out">Paid out — hold until the money lands</option>
                </select>
              </Field>
            </section>

            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" />LoanDisk posting ids</h4>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GoCardless Direct Debit (BECS) · method id"><Input {...num("method_id")} data-testid="lra-settings-gc-method" /></Field>
                <Field label="GoCardless PayTo · method id"><Input {...num("payto_method_id")} data-testid="lra-settings-payto-method" /></Field>
                <Field label="GoCardless · collector id"><Input {...num("collector_id")} data-testid="lra-settings-gc-collector" /></Field>
                <Field label="Capay bank credit · method id"><Input {...num("capay_method_id")} data-testid="lra-settings-capay-method" /></Field>
                <Field label="Capay bank credit · collector id"><Input {...num("capay_collector_id")} data-testid="lra-settings-capay-collector" /></Field>
              </div>
              <p className="text-[11px] text-muted-foreground">Defaults: Direct Debit {data.defaults?.method_id} · PayTo {data.defaults?.payto_method_id} · Online Transfer {data.defaults?.capay_method_id} (Capay) · collector {data.defaults?.collector_id} — the LoanDisk AU repayment methods the team keys under. GC BECS debits post as Direct Debit, GC PayTo as PayTo, Capay bank credits as Online Transfer.</p>
            </section>

            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" />Capay environment</h4>
              <div className="flex rounded-md border overflow-hidden text-xs w-fit" data-testid="lra-settings-capay-mode">
                {["sandbox", "live"].map((m) => (
                  <button key={m} type="button" disabled={!canEdit} onClick={() => set("capay_mode")(m)} data-testid={`lra-settings-capay-mode-${m}`}
                    className={`px-3 py-1.5 transition ${form.capay_mode === m ? (m === "live" ? "bg-red-600 text-white" : "bg-slate-800 text-white") : "bg-white text-slate-600 hover:bg-slate-50"} disabled:cursor-not-allowed`}>
                    {m === "live" ? "Live" : "Sandbox"}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Currently <b>{data.capay_mode}</b>{data.capay_configured ? "" : " · credentials missing"}. Same switch as Admin Settings → Capay; it also drives AUS payouts. Credentials for each environment live in Admin Settings.
              </p>
              <div className="space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2">
                <SyncLine label="GoCardless AU" run={data.last_sync} testid="lra-settings-last-sync-gc" />
                <SyncLine label="Capay bank feed" run={data.last_sync_capay} testid="lra-settings-last-sync-capay" />
              </div>
              {data.updated_at && <p className="text-[11px] text-muted-foreground">Last changed by {data.updated_by} · {fmtSydney(data.updated_at)}</p>}
            </section>
          </div>
        )}
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lra-settings-close">Close</Button>
          {canEdit && (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={saving || !data} data-testid="lra-settings-save">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save settings
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
